<script setup lang="ts">
  import { Folder as FolderIcon, FolderOpen } from 'lucide-vue-next'
  import type { FolderTreeNode } from '../models/FolderTreeNode'

  const props = defineProps<{
    node: FolderTreeNode
    depth: number
    selectedFolderId: string | null
  }>()

  const emit = defineEmits<{
    'folder-selected': [folderId: string | null]
  }>()

  function handleClick() {
    emit('folder-selected', props.node.id)
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      emit('folder-selected', props.node.id)
    }
  }

  function isActive(): boolean {
    return props.selectedFolderId === props.node.id
  }

  function handleChildSelected(folderId: string | null) {
    emit('folder-selected', folderId)
  }
</script>

<template>
  <div>
    <div
      class="folder-item"
      :class="{ 'folder-item--active': isActive() }"
      :style="{ paddingLeft: depth * 16 + 12 + 'px' }"
      role="button"
      tabindex="0"
      :aria-label="node.name"
      @click="handleClick"
      @keydown="handleKeydown"
    >
      <span
        v-if="node.color"
        class="folder-item__color-dot"
        :style="{ backgroundColor: node.color }"
      />
      <FolderOpen v-if="isActive()" class="folder-item__icon" :size="16" />
      <FolderIcon v-else class="folder-item__icon" :size="16" />
      <span class="folder-item__label">{{ node.name }}</span>
    </div>
    <template v-if="node.children && node.children.length > 0">
      <FolderTreeNodeItem
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
        :selected-folder-id="selectedFolderId"
        @folder-selected="handleChildSelected"
      />
    </template>
  </div>
</template>

<style scoped>
  .folder-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.15s ease;
  }

  .folder-item:hover {
    background-color: var(--color-background-soft, #f5f5f5);
  }

  .folder-item:focus-visible {
    outline: 2px solid #1677ff;
    outline-offset: -2px;
  }

  .folder-item--active {
    background-color: #e6f4ff;
    font-weight: 500;
  }

  .folder-item--active:hover {
    background-color: #bae0ff;
  }

  .folder-item__icon {
    flex-shrink: 0;
    color: var(--color-text-secondary, #666666);
  }

  .folder-item--active .folder-item__icon {
    color: #1677ff;
  }

  .folder-item__label {
    font-size: 13px;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .folder-item__color-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
</style>
